import { useCallback, useEffect, useState } from 'react';
import { Loader2, Package, AlertCircle, Play } from 'lucide-react';
import { apiFetch } from './apiBase';

const SCOPES = [
  { id: 'saas-tool', label: 'Shared reports', hint: 'Common Oracle reports used by this tool' },
  { id: 'adp', label: 'ADP Reconcile only', hint: 'Employee Master + Element Entries' },
  { id: 'both', label: 'All reports', hint: 'Shared reports plus Employee Master and Element Entries' },
];

export default function CatalogInstall({ activeConnection = null }) {
  const [scope, setScope] = useState('both');
  const [catalogRoot, setCatalogRoot] = useState('');
  const [adpFolder, setAdpFolder] = useState('');
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [publishToOracle, setPublishToOracle] = useState(true);
  const [error, setError] = useState('');

  const loadStatus = useCallback(
    async (rootOverride) => {
      const root = rootOverride ?? catalogRoot;
      setLoading(true);
      setError('');
      try {
        const data = await apiFetch(
          `/api/catalog/install/status?scope=${encodeURIComponent(scope)}&catalogRoot=${encodeURIComponent(root)}&adpFolder=${encodeURIComponent(adpFolder)}`
        );
        setStatus(data);
        if (typeof data.catalogRoot === 'string' && data.catalogRoot !== catalogRoot) {
          setCatalogRoot(data.catalogRoot);
        }
        if (typeof data.adpFolder === 'string' && data.adpFolder !== adpFolder) {
          setAdpFolder(data.adpFolder);
        }
      } catch (e) {
        setError(e.message);
        setStatus(null);
      } finally {
        setLoading(false);
      }
    },
    [scope, catalogRoot, adpFolder]
  );

  useEffect(() => {
    const root = String(activeConnection?.oracleBiCatalogRoot || '').replace(/\/+$/, '');
    if (root) {
      setCatalogRoot(root);
      setAdpFolder(`${root}/ADP`);
    }
  }, [activeConnection?.id, activeConnection?.oracleBiCatalogRoot]);

  useEffect(() => {
    loadStatus();
    // Reload when scope or active tenant changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, activeConnection?.id]);

  async function runInstall() {
    setInstalling(true);
    setError('');
    setResult(null);
    try {
      const data = await apiFetch('/api/catalog/install', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          catalogRoot,
          adpFolder,
          mergeEnv: false,
          publishToOracle,
        }),
      });
      setResult(data);
      await loadStatus();
    } catch (e) {
      if (e.data?.validations || e.data?.oracle) {
        setResult(e.data);
        if (!e.data?.oracle && e.message) setError(e.message);
        await loadStatus();
      } else {
        setError(e.message);
      }
    } finally {
      setInstalling(false);
    }
  }

  const validations = result?.validations || status?.validations || [];
  const missingPackages = validations.filter((pkg) => !pkg.ok);
  const ready = status?.ok;
  const activeHost = String(activeConnection?.baseUrl || '')
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '');
  const canPublish = !!(activeConnection?.id && activeConnection?.baseUrl);

  return (
    <div className="card-panel space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-100">
            <Package className="h-5 w-5 text-cyan-400" />
            Install reports on this Oracle tenant
          </div>
          <p className="mt-1 text-sm text-slate-400">
            Publish the payroll reports to the active Oracle tenant so Employee Master,
            Element Entries, and related extracts can run.
          </p>
        </div>
        <button
          type="button"
          onClick={loadStatus}
          disabled={loading}
          className="btn-secondary text-xs"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>

      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Install scope
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {SCOPES.map((s) => (
            <label
              key={s.id}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                scope === s.id
                  ? 'border-cyan-600/60 bg-cyan-950/30'
                  : 'border-slate-700/60 bg-slate-900/20'
              }`}
            >
              <input
                type="radio"
                name="catalog-scope"
                checked={scope === s.id}
                onChange={() => setScope(s.id)}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-slate-200">{s.label}</span>
                <span className="block text-xs text-slate-500">{s.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={publishToOracle}
            onChange={(e) => setPublishToOracle(e.target.checked)}
          />
          Publish to Oracle on this tenant
          {canPublish ? ` (${activeConnection.name})` : ''}
        </label>
      </div>

      {canPublish ? (
        <div className="alert-success">
          Reports will be published to <strong>{activeConnection.name}</strong>
          {activeHost ? ` (${activeHost})` : ''} as {activeConnection.username}. After install,
          open BI Publisher on that tenant to confirm the ADP reports.
        </div>
      ) : (
        <div className="alert-warning">
          Click <strong>Use this</strong> on the tenant you want first. Reports install to that
          Oracle connection only.
        </div>
      )}

      {error && (
        <div className="alert-error flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loading && !status ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking reports…
        </div>
      ) : missingPackages.length > 0 ? (
        <div className="alert-warning">
          Some required reports are missing. Ask your consultant to restore the shipped catalog
          package.
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={runInstall}
          disabled={installing || loading || (publishToOracle && !canPublish)}
          className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:opacity-50"
        >
          {installing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {publishToOracle ? 'Install to Oracle' : 'Prepare only (do not publish)'}
        </button>
        {ready && !result && (
          <span className="text-sm text-emerald-700">Ready to install</span>
        )}
        {result && (
          <span
            className={`text-sm ${
              result.oracle?.ok || (result.ok && !publishToOracle)
                ? 'text-emerald-700'
                : 'text-amber-800'
            }`}
          >
            {result.oracle?.ok
              ? `Published ${result.oracle.publishedCount} report(s) to ${
                  result.oracle.target?.name || activeConnection?.name || 'Oracle'
                }`
              : result.oracle
                ? `Publishing to ${result.oracle.target?.name || 'Oracle'} had ${
                    result.oracle.failedCount
                  } error(s)`
                : result.ok
                  ? 'Prepared without publishing'
                  : 'Install finished, but some reports are missing'}
          </span>
        )}
      </div>

      {result?.oracle?.target?.host && (
        <p className="text-xs text-slate-400">
          Target tenant: {result.oracle.target.name} · {result.oracle.target.host} ·{' '}
          {result.oracle.target.username}
        </p>
      )}

      {result?.oracle?.listingError && (
        <p className="alert-warning text-xs">
          Published, but the ADP reports could not be listed. Open BI Publisher to confirm.
        </p>
      )}

      {result?.oracle?.ok && (
        <div className="alert-success">
          <div className="mb-2 text-sm font-medium">What to do next</div>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>Open Reports and run Employee Master and Element Entries.</li>
          </ol>
        </div>
      )}
    </div>
  );
}
